/**
 * The platform's password rule, mirrored for the admin panel's forms.
 *
 * 140: mirrors `backend/src/shared/passwordPolicy.ts`. The server has enforced this on
 * `PATCH /users/me/password` since 035, so the admin screens were already subject to it — they simply
 * never said so, and the refusal they surfaced was the literal string **"ValidationError"**, because
 * both API clients read `payload.error ?? payload.message` and the server put the readable text in
 * `message`. An administrator's only clue was therefore a word that is not a sentence.
 *
 * 🔴 The server remains the enforcing side. This is stated wording plus a pre-submit check, so nobody
 * has to discover the rule by being rejected. `POST /users`'s `temp_password` gained the same rule at
 * the boundary in this feature, having previously accepted any eight characters.
 */

export const PASSWORD_MIN_LENGTH = 8;

/** Shown beside every field where an administrator sets a password. */
export const PASSWORD_RULE_TEXT =
  'At least 8 characters, with one uppercase letter and one number.';

/** Matches the backend predicate exactly. */
export function satisfiesPasswordPolicy(candidate: string): boolean {
  return (
    candidate.length >= PASSWORD_MIN_LENGTH && /[A-Z]/.test(candidate) && /[0-9]/.test(candidate)
  );
}

/**
 * The message shown when a password misses — the rule itself, because someone who has just failed
 * needs to know what is wanted rather than only that they were wrong.
 */
export const PASSWORD_INVALID_MESSAGE = PASSWORD_RULE_TEXT;
